function get_first_non_empty_value(values = []) { return values.find((value) => String(value || "").trim()) || ""; }
function get_nested_candidate_lists(value) { return [value?.data, value?.results, value?.items, value?.applications, value?.applicants, value?.submitted_applications, value?.submitted_applicants, value?.list_pending_entities]; }
function get_nested_candidate_numbers(value) { return [value?.count, value?.total, value?.length, value?.total_count, value?.applicants_count, value?.applications_count, value?.submitted_count]; }
function get_advert_title_key(advert) { return get_first_non_empty_value([advert?.advert_title, advert?.title, advert?.job_title, advert?.name]).toLowerCase(); }
function get_advert_reference_key(advert) { return get_first_non_empty_value([advert?.reference_number, advert?.reference, advert?.ref_number]).toLowerCase(); }
function normalize_advert_status_label(raw_status) {
  const normalized = raw_status.toLowerCase();
  const status_labels = {
    published: "Active",
    open: "Active",
    active: "Active",
    closed: "Closed",
    expired: "Closed",
    draft: "Draft",
    pending: "Draft",
  };
  return Reflect.get(status_labels, normalized) || "";
}
function has_advert_period_values(start, end) { return !!(start || end); }
function build_advert_period(start, end) { return has_advert_period_values(start, end) ? `${start || "-"} -> ${end || "-"}` : "-"; }
function get_numeric_candidate_count(value) { const numeric = Number(value); return Number.isFinite(numeric) ? numeric : null; }
function is_object_candidate(value) { return !!value && typeof value === "object"; }
function get_advert_count_candidates(advert) { return [advert?.submitted_count, advert?.submitted_applicants_count, advert?.submitted_applicant_count, advert?.submitted_applications_count, advert?.submitted_application_count, advert?.total_submitted_applicants, advert?.total_submitted_applications, advert?.total_applicants, advert?.total_applications, advert?.applicants_count, advert?.applicant_count, advert?.applications_count, advert?.application_count, advert?.applicants, advert?.applications, advert?.submitted_applicants, advert?.submitted_applications, advert?.applicant_list, advert?.application_list]; }
function get_matching_store_count(get_advert_count_from_store, store, key) { return key ? get_advert_count_from_store(store, key) : null; }
function get_advert_store_count(context, advert, storeKey, keyBuilder) {
  const key = keyBuilder(advert);
  const store = Reflect.get(context.advert_applicant_counts || {}, storeKey);
  return key ? get_matching_store_count(context.get_advert_count_from_store, store, key) : null;
}
function is_empty_count_candidate(value) { return value === null || value === undefined || value === ""; }
function get_advert_status_raw_value(advert) { return String(advert?.status || advert?.approval_status || "").trim(); }
function get_advert_status_fallback(raw_status) { return `${raw_status.charAt(0).toUpperCase()}${raw_status.slice(1)}`; }
function get_candidate_list_count(value) {
  const matching_list = get_nested_candidate_lists(value).find((entry) => Array.isArray(entry));
  return matching_list ? matching_list.length : null;
}
function get_candidate_numeric_count(value) {
  const matching_count = get_nested_candidate_numbers(value).find((entry) => Number.isFinite(Number(entry)));
  return matching_count !== undefined ? Number(matching_count) : null;
}
function get_object_candidate_count(value) {
  const list_count = get_candidate_list_count(value);
  return list_count !== null ? list_count : get_candidate_numeric_count(value);
}
function get_candidate_array_count(value) { return Array.isArray(value) ? value.length : null; }
function get_candidate_object_count(value) { return is_object_candidate(value) ? get_object_candidate_count(value) : null; }
function resolve_candidate_count(value) {
  if (is_empty_count_candidate(value)) return null;
  const resolvers = [get_candidate_array_count, get_numeric_candidate_count, get_candidate_object_count];
  return resolvers.map((resolver) => resolver(value)).find((count) => count !== null) ?? null;
}
function get_store_count_sources(context, advert) {
  const advert_id = Number(advert?.id || advert?.advert_id || 0);
  return [
    advert_id > 0 ? context.get_advert_count_from_store(context.advert_applicant_counts?.byId, advert_id) : null,
    get_advert_store_count(context, advert, "byTitle", get_advert_title_key),
    get_advert_store_count(context, advert, "byReference", get_advert_reference_key),
  ];
}
function normalize_advert_date_input(value) {
  const raw = String(value || "").trim();
  return raw === "-" ? "" : raw;
}

export default {
  computed: {
    normalized_job_adverts() {
      return (this.job_adverts || []).map((advert) => ({
        ...advert,
        display_title: this.get_advert_title(advert),
        display_profile: this.get_advert_profile_name(advert),
        display_reference: this.get_advert_reference(advert),
        display_status: this.get_advert_status(advert),
        display_period: this.get_advert_period(advert),
        display_vacancies: this.get_advert_vacancies(advert),
        display_applicants: this.get_advert_applicants_count(advert),
      }));
    },
    filtered_job_adverts_source() {
      const query = String(this.advert_search || "").trim().toLowerCase();
      return (this.job_adverts || []).filter((advert) => {
        return !query || [this.get_advert_title(advert), this.get_advert_profile_name(advert), this.get_advert_reference(advert)]
          .some((value) => String(value || "").toLowerCase().includes(query));
      });
    },
    filtered_job_adverts() {
      const query = String(this.advert_search || "").trim().toLowerCase();
      return this.normalized_job_adverts.filter((advert) => {
        return !query || [advert.display_title, advert.display_profile, advert.display_reference]
          .some((value) => String(value || "").toLowerCase().includes(query));
      });
    },
    advert_total_pages() { return Math.max(1, Math.ceil(this.filtered_job_adverts.length / this.advert_page_size)); },
    paginated_job_adverts_source() {
      const start = (this.advert_page - 1) * this.advert_page_size;
      return this.filtered_job_adverts_source.slice(start, start + this.advert_page_size);
    },
    paginated_job_adverts() {
      const start = (this.advert_page - 1) * this.advert_page_size;
      return this.filtered_job_adverts.slice(start, start + this.advert_page_size);
    },
    has_advert_vacancies() { return this.normalized_job_adverts.some((advert) => advert.display_vacancies !== "-"); },
    advert_status_counts() {
      const counts = { all: this.normalized_job_adverts.length, active: 0, draft: 0, closed: 0 };
      this.normalized_job_adverts.forEach((advert) => {
        const status = String(advert.display_status || "").toLowerCase();
        if (Reflect.has(counts, status)) Reflect.set(counts, status, Reflect.get(counts, status) + 1);
      });
      return counts;
    },
  },
  watch: {
    advert_search() { this.advert_page = 1; },
    "$route.query.all_adverts"(value) {
      if (value === "true") {
        this.advert_page = 1;
        this.active_tab = "Job Adverts";
        localStorage.setItem("active_tab", "Job Adverts");
        this.get_job_adverts();
      }
    },
  },
  methods: {
    get_advert_title_candidates(advert) { return [advert?.advert_title, advert?.title, advert?.name, advert?.job_title, advert?.advert_name]; },
    get_advert_title(advert) { return this.get_advert_title_candidates(advert).find((value) => String(value || "").trim()) || "Untitled Advert"; },
    get_advert_profile_name_candidates(advert) { return [advert?.entity_type_name, advert?.job_profile, advert?.profile_name, this.entity_type_name]; },
    get_advert_profile_name(advert) { return this.get_advert_profile_name_candidates(advert).find((value) => String(value || "").trim()) || "Job Profile"; },
    get_advert_reference(advert) { return advert?.reference_number || advert?.reference || advert?.ref_number || "-"; },
    get_advert_status(advert) {
      const raw_status = get_advert_status_raw_value(advert);
      if (!raw_status) return "Draft";
      const normalized_status = normalize_advert_status_label(raw_status);
      return normalized_status || get_advert_status_fallback(raw_status);
    },
    build_advert_period_label(start, end) { return build_advert_period(start, end); },
    get_advert_period(advert) {
      const start = this.format_advert_date(advert?.published_date || advert?.opening_date || advert?.open_date);
      const end = this.format_advert_date(advert?.closing_date || advert?.close_date);
      return this.build_advert_period_label(start, end);
    },
    has_valid_vacancy_value(vacancies, parsed) { return !(vacancies === null || vacancies === undefined || vacancies === "" || !Number.isFinite(parsed)); },
    get_advert_vacancies(advert) {
      const vacancies = advert?.vacancies ?? advert?.vacancy_count ?? advert?.number_of_posts;
      const parsed = Number(vacancies);
      return this.has_valid_vacancy_value(vacancies, parsed) ? parsed : 0;
    },
    get_count_from_candidate(value) {
      return resolve_candidate_count(value);
    },
    get_nested_candidate_list_count(value) {
      return get_candidate_list_count(value);
    },
    get_nested_candidate_numeric_count(value) {
      return get_candidate_numeric_count(value);
    },
    get_advert_count_from_store(store, key) {
      const count = Number(Reflect.get(store || {}, key));
      return Number.isFinite(count) ? count : null;
    },
    get_advert_count_from_candidates(candidates = []) {
      for (const candidate of candidates) {
        const count = this.get_count_from_candidate(candidate);
        if (count !== null) return count;
      }
      return 0;
    },
    get_advert_applicants_count(advert) {
      const store_count = get_store_count_sources(this, advert).find((count) => count !== null);
      if (store_count !== undefined) return store_count || 0;
      return this.get_advert_count_from_candidates(get_advert_count_candidates(advert));
    },
    set_advert_status_filter(status) { this.advert_status_filter = status; },
    format_valid_advert_date(date) {
      const day = String(date.getDate()).padStart(2, "0");
      const month = date.toLocaleString("en-ZA", { month: "short" });
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    },
    format_advert_date(value) {
      if (!value) return "";
      const raw = normalize_advert_date_input(value);
      if (!raw) return "";
      const date = new Date(raw);
      return Number.isNaN(date.getTime()) ? raw : this.format_valid_advert_date(date);
    },
    set_advert_page(page) {
      const next_page = Math.min(Math.max(Number(page) || 1, 1), this.advert_total_pages);
      this.advert_page = next_page;
    },
  },
};
